import { useState } from "react";
import VideoDrmPlayer from "../../../../../../../features/video_feature/video_feature_content_component/VideoDrmPlayer";
import liveIcon from "../../../../../../../../../utilities/images/package_course_story/icon-live.svg";
import doubtIcon from "../../../../../../../../../utilities/images/package_course_story/icon-doubt.svg";
import recordedIcon from "../../../../../../../../../utilities/images/package_course_story/icon-recorded.svg";
import mockIcon from "../../../../../../../../../utilities/images/package_course_story/icon-mock.svg";
import cpsIcon from "../../../../../../../../../utilities/images/package_course_story/icon-cps.svg";
import calendarIcon from "../../../../../../../../../utilities/images/package_course_story/icon-calendar.svg";
import playButton from "../../../../../../../../../utilities/images/package_course_story/play-button.svg";
import avatarPlaceholder from "../../../../../../../../../utilities/images/package_course_story/avatar-placeholder.svg";
import { buildCourseStory } from "./courseStoryModel";
import "./StoreCourseStory.scss";

const FEATURE_STYLES = [
  {
    key: "live",
    icon: liveIcon,
    background: "linear-gradient(135deg, #ff775c 0%, #ffc929 100%)",
  },
  {
    key: "doubt",
    icon: doubtIcon,
    background: "linear-gradient(180deg, #e31d30 0%, #ff6b6b 100%)",
  },
  {
    key: "recorded",
    icon: recordedIcon,
    background: "linear-gradient(135deg, #6598ff 0%, #2970ff 100%)",
  },
  {
    key: "mock",
    icon: mockIcon,
    background: "linear-gradient(180deg, #48a758 0%, #1a8516 100%)",
  },
  {
    key: "cps",
    icon: cpsIcon,
    background: "linear-gradient(135deg, #e65cff 0%, #8d29ff 100%)",
  },
  {
    key: "schedule",
    icon: calendarIcon,
    background: "linear-gradient(180deg, #f28e43 0%, #e94949 100%)",
  },
];

const matchedStyle = (feature) => {
  const haystack = `${feature.type} ${feature.title}`.toLowerCase();
  if (/\blive\b/.test(haystack)) return { ...FEATURE_STYLES[0], recorded: false };
  if (/doubt|mentor|one-to-one|one to one/.test(haystack)) {
    return { ...FEATURE_STYLES[1], recorded: false };
  }
  if (/record|revision/.test(haystack)) {
    return { ...FEATURE_STYLES[2], recorded: true };
  }
  if (/mock|test/.test(haystack)) return { ...FEATURE_STYLES[3], recorded: false };
  if (/\bcps\b|concept|practice/.test(haystack)) {
    return { ...FEATURE_STYLES[4], recorded: false };
  }
  if (/schedule|disciplin|calendar|goal|habit/.test(haystack)) {
    return { ...FEATURE_STYLES[5], recorded: false };
  }
  return null;
};

const styleForFeature = (feature, index) =>
  matchedStyle(feature) || { ...FEATURE_STYLES[index % FEATURE_STYLES.length], recorded: false };

const isRemoteIcon = (icon) => typeof icon === "string" && /^(https?:|data:|\/)/.test(icon);

const youtubeIdFrom = (url) => {
  const match = String(url || "").match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{6,})/
  );
  return match ? match[1] : "";
};

const FeatureIcon = ({ feature, index }) => {
  const style = styleForFeature(feature, index);
  const remote = isRemoteIcon(feature.icon);

  return (
    <span
      className={`course-story__feature-icon${
        style.recorded ? " course-story__feature-icon--recorded" : ""
      }`}
      style={{ backgroundImage: style.background }}
    >
      {remote ? (
        <img src={feature.icon} alt="" className="course-story__feature-icon-remote" />
      ) : (
        <img src={style.icon} alt="" />
      )}
      {style.recorded ? <span className="course-story__rec">Rec</span> : null}
    </span>
  );
};

const StoryVideo = ({ video }) => {
  const [playing, setPlaying] = useState(false);
  const youtubeId = youtubeIdFrom(video.url);
  const canPlay = Boolean(video.videoId || video.url);

  return (
    <div
      className={`course-story__video${playing ? " is-playing" : ""}`}
      style={
        video.thumbnail && !playing
          ? { backgroundImage: `url(${video.thumbnail})` }
          : undefined
      }
    >
      {playing && youtubeId ? (
        <iframe
          className="course-story__player-video"
          src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
          title={video.title}
          allow="autoplay; encrypted-media"
          allowFullScreen
        />
      ) : playing && video.videoId && !video.url ? (
        <div className="course-story__player">
          <VideoDrmPlayer id_video={video.videoId} />
        </div>
      ) : playing && video.url ? (
        <video className="course-story__player-video" src={video.url} controls autoPlay />
      ) : (
        <>
          <p className={`course-story__video-title${video.thumbnail ? " has-photo" : ""}`}>
            {video.title}
          </p>
          {canPlay ? (
            <button
              type="button"
              className="course-story__play"
              aria-label={`Play ${video.title}`}
              onClick={() => setPlaying(true)}
            >
              <img src={playButton} alt="" />
            </button>
          ) : null}
        </>
      )}
    </div>
  );
};

const StudentAvatar = ({ image, name }) => {
  const [failed, setFailed] = useState(false);
  if (!image || failed) return <img src={avatarPlaceholder} alt="" />;
  return (
    <img
      src={image}
      alt={name || ""}
      className="course-story__avatar-photo"
      onError={() => setFailed(true)}
    />
  );
};

const StoreCourseStory = ({ packageData }) => {
  const story = buildCourseStory(packageData);
  if (!story.hasContent) return null;

  return (
    <section className="course-story" aria-label="Course description">
      {story.videos.map((video) => (
        <StoryVideo key={video.id} video={video} />
      ))}

      {story.featureSections.map((section) => (
        <div key={section.id} className="course-story__block">
          <div className="course-story__block-head">
            <h2 className="course-story__heading">{section.title}</h2>
            {section.subtitle ? (
              <p className="course-story__subheading">{section.subtitle}</p>
            ) : null}
          </div>
          <div className="course-story__features">
            {section.features.map((feature, index) => (
              <article key={feature.id} className="course-story__feature">
                <FeatureIcon feature={feature} index={index} />
                <div className="course-story__feature-copy">
                  {feature.title ? <h3>{feature.title}</h3> : null}
                  {feature.description ? <p>{feature.description}</p> : null}
                </div>
              </article>
            ))}
          </div>
        </div>
      ))}

      {story.testimonials.length ? (
        <div className="course-story__block">
          <h2 className="course-story__heading">What Students Say</h2>
          <div className="course-story__testimonials">
            {story.testimonials.map((item) => (
              <article key={item.id} className="course-story__testimonial">
                <div className="course-story__student">
                  <StudentAvatar image={item.image} name={item.name} />
                  <div className="course-story__student-meta">
                    {item.name ? <p className="course-story__student-name">{item.name}</p> : null}
                    {item.rank ? <span className="course-story__rank">{item.rank}</span> : null}
                  </div>
                </div>
                {item.quote ? <p className="course-story__quote">{item.quote}</p> : null}
              </article>
            ))}
          </div>
        </div>
      ) : null}

      {story.stats.length ? (
        <div className="course-story__stats">
          {story.stats.map((stat) => (
            <article key={stat.id} className="course-story__stat">
              <p className="course-story__stat-value">{stat.value}</p>
              <p className="course-story__stat-label">{stat.label}</p>
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
};

export default StoreCourseStory;
